// What state an invoice's parse is actually in.
//
// This lived inside InboxPage. The Invoices tab on the site page then grew its
// own four-line version of the same idea — parsed/parsing/failed/pending, with
// no notion of a parse that DIED — and a row whose background parse never
// finished sat on "Parsing…" indefinitely with nothing on screen to say so.
// Two derivations of one fact, and the weaker one was in front of the user.
//
// So it lives here, once, and both surfaces read it. InboxPage re-exports it for
// its existing tests.
//
// The inputs are structural on purpose: the inbox holds an `InboxEmail`, the
// Invoices tab holds its own row shape, and neither needs to know about the
// other to ask this question.

/** An attachment as far as parse state is concerned. */
export type ParseAttachmentLike = {
  parsing_status: string;
  parse_error?: string | null;
  updated_at?: string | null;
};

/** The email carrying those attachments. */
export type ParseSubjectLike = {
  bill_id?: string | null;
  assigned_at?: string | null;
  attachments?: ParseAttachmentLike[] | null;
};

export type ParseState = 'none' | 'pending' | 'parsing' | 'stalled' | 'parsed' | 'failed';

// How often a running parse touches `updated_at` purely to prove it is alive.
// Server-side (`billEmailParseService`) writes on this cadence; everything below
// interprets the age of that write. Before this existed, `updated_at` moved only
// at the two status transitions, so a parse mid-way through a 5-minute LlamaCloud
// poll and a parse whose process had been killed were byte-for-byte identical in
// the database — which is why an elapsed counter alone could never distinguish
// them, and why the chip counted up to 15m on a corpse.
export const PARSE_HEARTBEAT_MS = 20 * 1000; // 20 seconds

// Hard wall-clock bound on one parse attempt, enforced server-side. Sized above
// the pipeline's own worst case (LlamaCloud caps at 5 min, the LLM at 60s x 3
// retries, plus the repair pass) so it only ever fires on a genuine hang, never
// on a slow-but-healthy invoice.
export const PARSE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

// An attachment in 'downloading'/'parsing' whose heartbeat is older than this is
// treated as stalled (the parse died) rather than still in progress. Single
// source of truth — do not repeat the literal in a component (US-009).
//
// Was 2 hours, because without a heartbeat that was the only interval long
// enough not to libel a healthy parse. With one, six missed beats is decisive,
// and the user sees the truth in ~2 minutes instead of staring at a climbing
// counter for two hours with the Retry button disabled the whole time.
export const PARSE_STALL_THRESHOLD_MS = 6 * PARSE_HEARTBEAT_MS; // 2 minutes

// The separate, much looser bound for an attachment that is still 'pending'
// after its email was assigned (job 68). Nothing is heartbeating a row that has
// not started, so its age says only "no worker has picked this up yet" — which
// is normal for a short while and suspicious after ten minutes. Kept distinct
// from the active threshold on purpose: reusing 2 minutes here would call a
// queued parse dead before anything had a chance to claim it.
export const PARSE_PENDING_STALL_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes

const msSince = (iso: string | null | undefined, now: number): number | null => {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  return Number.isNaN(ts) ? null : now - ts;
};

export function parseStateOf(subject: ParseSubjectLike, now: number = Date.now()): ParseState {
  const atts = subject.attachments ?? [];
  if (atts.length === 0) return 'none';
  if (atts.some((a) => a.parsing_status === 'failed')) return 'failed';

  const active = atts.filter(
    (a) => a.parsing_status === 'downloading' || a.parsing_status === 'parsing',
  );
  if (active.length > 0) {
    // Stalled only when EVERY active attachment has been untouched past the
    // threshold — a fresh one still counts as genuinely parsing.
    const allStale = active.every((a) => {
      const age = msSince(a.updated_at, now);
      return age !== null && age > PARSE_STALL_THRESHOLD_MS;
    });
    return allStale ? 'stalled' : 'parsing';
  }

  if (atts.every((a) => a.parsing_status === 'parsed')) return 'parsed';

  // Job 68: "stuck in Pending ... for almost days to weeks". Pending is the
  // correct, indefinite state for an email nobody has assigned yet — there is
  // nothing to parse until it has a bill. Once it IS assigned the parse should
  // start within seconds, so a still-pending attachment past the same threshold
  // means the background parse never ran (a crash before it could record a
  // failure, or a server restart). Reported as stalled so it shows a reason and
  // offers Retry, rather than looking identical to a normal wait.
  if (subject.bill_id && subject.assigned_at) {
    const ages = [
      msSince(subject.assigned_at, now),
      ...atts.map((a) => msSince(a.updated_at, now)),
    ].filter((age): age is number => age !== null);
    // The most recent activity, so a re-parse kicked off just now is not called
    // stalled because the assignment happened months ago.
    const sinceLastTouch = ages.length > 0 ? Math.min(...ages) : null;
    if (sinceLastTouch !== null && sinceLastTouch > PARSE_PENDING_STALL_THRESHOLD_MS) return 'stalled';
  }

  return 'pending';
}

/**
 * The first parse error recorded against a subject, for display.
 *
 * `bv_invoice_email_attachment.parse_error` has been written since job 68 and
 * shown nowhere: a failed row read "Parse failed" and stopped, so the one thing
 * the user needed — why — was in the database and not on the screen (job 76).
 */
export function parseErrorOf(subject: ParseSubjectLike): string | null {
  const failed = (subject.attachments ?? []).find(
    (a) => a.parsing_status === 'failed' && !!a.parse_error,
  );
  return failed?.parse_error ?? null;
}

/**
 * How long the active parse has been running, in ms, or null if none is.
 *
 * "It keeps showing the parsing text, I don't know what is happening in the
 * background" — a label that never changes reads as a hung screen even when the
 * work is fine. An elapsed count is the cheapest honest signal that something is
 * still moving, and it also makes a slow parse distinguishable from a dead one
 * long before the stall threshold is reached.
 *
 * Measured from the most recently touched active attachment, which is the last
 * time the parse actually reported progress (pending → downloading → parsing).
 */
export function activeParseElapsedMs(
  subject: ParseSubjectLike,
  now: number = Date.now(),
): number | null {
  if (parseStateOf(subject, now) !== 'parsing') return null;
  const ages = (subject.attachments ?? [])
    .filter((a) => a.parsing_status === 'downloading' || a.parsing_status === 'parsing')
    .map((a) => msSince(a.updated_at, now))
    .filter((age): age is number => age !== null && age >= 0);
  return ages.length > 0 ? Math.min(...ages) : null;
}

/** `45s`, `3m`, `1h 12m` — a duration short enough to sit inside a status chip. */
export function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours}h` : `${hours}h ${remainder}m`;
}

/**
 * Whether anything here is mid-parse, so a view can poll while it is and stop
 * when it is not.
 *
 * Deliberately excludes 'stalled': a stalled parse is not going to resolve on
 * its own, and polling for it forever is a background request every few seconds
 * for as long as the tab is open.
 */
export function hasActiveParse(subjects: ParseSubjectLike[], now: number = Date.now()): boolean {
  return subjects.some((s) => parseStateOf(s, now) === 'parsing');
}

// How long a view keeps polling after asking for a parse, before the attachment
// has been claimed and written as 'downloading'.
//
// Sized for the gap between the endpoint answering and the claim landing — two
// database round trips — with room for a slow one, not for the parse itself.
// `hasActiveParse` takes over the moment the claim is visible.
export const PARSE_HANDOFF_GRACE_MS = 60 * 1000; // 1 minute

/**
 * Whether a view should be polling for parse progress.
 *
 * `hasActiveParse` alone was not enough, and the gap was a race: the retry and
 * assign endpoints return as soon as the parse is HANDED OFF, and
 * `parseAttachmentIntoBill` claims the attachment after that. The refresh fired
 * straight after the call therefore still reads 'pending' or 'failed' — nothing
 * is active yet — so the poll meant to follow that parse was never armed, and
 * the row kept its old status until the page was reloaded by hand.
 *
 * `handoffUntil` closes it: asking for a parse is itself reason to poll, for as
 * long as the claim could still be in flight. 0 means nothing was handed off.
 */
export function shouldPollParses(
  subjects: ParseSubjectLike[],
  handoffUntil: number,
  now: number = Date.now(),
): boolean {
  return hasActiveParse(subjects, now) || now < handoffUntil;
}
